import { getConfig } from '@/configs/config-accessor';
import { handleGetCollection } from '@/constants/db-collection.constants';
import ApiService from './ApiService';
import { IProduct, IProductVariation } from './product-service';

// Get the API prefix from the config accessor
const getApiPrefix = () => getConfig().newApiPrefix;

export interface IMenu {
  _id: string;
  title: string;
  description: string;
  progression: number;
  isPublished: boolean;
  productTags: string[];
  timings: [Date, Date];
  categories: ICategory[];
}

export interface ICategory {
  _id: string;
  title: string;
  timings: [Date, Date] | null;
  products: IMenuProduct[];
}

export interface IMenuProduct {
  _id: string;
  tags: string[];
  product: IProduct;
  disabled: boolean;
  addons: Record<IProductVariation['_id'], IMenuProductAddon[]>;
}

export interface IMenuProductAddon {
  _id: string;
  title: string;
  limit: number;
  isRequired: boolean;
  isMultiselect: boolean;
  addonProducts: IMenuProductAddonProducts[];
}

export interface IMenuProductAddonProducts {
  _id: string;
  productId: IProduct['_id'];
  title: IProduct['title'];
  price: number;
  isSelected: boolean;
}

export type CreateMenu_Body = Omit<Partial<IMenu>, '_id'>;
export type CreateMenu_Response = IResponse<IMenu | null>;
export const createMenu = async (body: Partial<CreateMenu_Body>) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<CreateMenu_Response, DynamicCreate_Body<CreateMenu_Body>>({ 
    url: `${newApiPrefix}/dynamic/create`, 
    method: 'post', 
    data: { 
      collection: handleGetCollection('menu'), 
      data: body 
    } 
  });
};

export type GetMenuById_Params = { _id: string };
export type GetMenuById_Response = IResponse<IMenu | null>;
export const getMenuById = async (params: GetMenuById_Params) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<GetMenuById_Response>({ 
    url: `${newApiPrefix}/menus/${params._id}`, 
    method: 'get' 
  });
};

export type GetMenus_Response = IResponse<Array<Omit<IMenu, 'categories'>> | null>;
export const getMenusList = async () => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<GetMenus_Response, DynamicGet_Body>({ 
    url: `${newApiPrefix}/dynamic/get`, 
    method: 'post', 
    data: { 
      collection: handleGetCollection('menu'), 
      select: { categories: 0 } 
    } 
  });
};

export type UpdateMenu_Params = { _id: string };
export type UpdateMenu_Body = Partial<Omit<IMenu, '_id'> | unknown>;
export type UpdateMenu_Response = IResponse<IMenu | null>;
export const updateMenu = async (params: UpdateMenu_Params, body: UpdateMenu_Body) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<UpdateMenu_Response, DynamicUpdate_Body<UpdateMenu_Body>>({ 
    url: `${newApiPrefix}/dynamic/update`, 
    method: 'patch', 
    data: { 
      collection: handleGetCollection('menu'), 
      filter: { _id: params._id }, 
      data: body 
    } 
  });
};

export type DeleteMenu_Params = { _id: string };
export type DeleteMenu_Response = IResponse<IMenu | null>;
export const deleteMenu = async (params: DeleteMenu_Params) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<DeleteMenu_Response, DynamicDelete_Body>({ 
    url: `${newApiPrefix}/dynamicmongodb/delete`, 
    method: 'post', 
    data: { 
      collection: handleGetCollection('menu'), 
      filter: { _id: params._id } 
    } 
  });
};
